const HTML_ENTITY_MAP = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' '
};

const decodeNumericEntity = (match, decimal) => {
  const codePoint = Number(decimal);
  if (!Number.isFinite(codePoint)) {
    return match;
  }
  try {
    return String.fromCodePoint(codePoint);
  } catch (error) {
    return match;
  }
};

export const decodeHtmlEntities = (value = '') => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/&#(\d+);/g, decodeNumericEntity)
    .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (match) => HTML_ENTITY_MAP[match] || match)
    .replace(/\s+/g, (space) => (space.includes('\n') ? ' ' : space));
};

export const normalizeCategoryName = (name = '') => {
  const decoded = decodeHtmlEntities(name);
  return decoded.replace(/\s+/g, ' ').trim();
};

export const slugifyCategoryName = (name = '') => {
  const normalized = normalizeCategoryName(name).toLowerCase();
  return normalized
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
    .trim();
};

const CATEGORY_ICON_RULES = [
  // Special Categories
  { matcher: /best selling|popular|top/, icon: '🔥' },

  // Men's Health & ED
  { matcher: /sildenafil/, icon: '🔷' },
  { matcher: /tadalafil/, icon: '🟡' },
  { matcher: /vardenafil/, icon: '🟠' },
  { matcher: /dapoxetine/, icon: '⏱️' },
  { matcher: /erectile|ed\b/, icon: '⚡' },
  { matcher: /men's health|men\b/, icon: '👨' },

  // Mental Health & Neurology
  { matcher: /nootropic|brain|smart/, icon: '🧠' },
  { matcher: /anxiety|stress|calm/, icon: '🧘' },
  { matcher: /sleep|insomnia|zopiclone/, icon: '🌙' },

  // Respiratory
  { matcher: /asthma|respiratory|lung|breath/, icon: '🫁' },

  // Infection & Parasites
  { matcher: /worm|parasite|albendazole/, icon: '🪱' },
  { matcher: /antiviral|virus|flu/, icon: '🛡️' },
  { matcher: /antibiotic|bacterial|infection/, icon: '💊' },

  // Pain & General Health
  { matcher: /pain|analgesic|ache/, icon: '🩹' },
  { matcher: /fever|temperature/, icon: '🌡️' },
  
  // Veterinary
  { matcher: /veterinary|pet|animal/, icon: '🐾' },

  // Other Systems
  { matcher: /cardio|heart|blood pressure/, icon: '❤️' },
  { matcher: /skin|derma|hair|beauty/, icon: '✨' },
  { matcher: /women|female/, icon: '👩' },
  { matcher: /digestive|gut|stomach/, icon: '🩺' }
];

export const getCategoryIcon = (categoryInput = '') => {
  const target = typeof categoryInput === 'string'
    ? categoryInput
    : `${categoryInput?.slug || ''} ${categoryInput?.name || ''}`;

  const normalized = normalizeCategoryName(target).toLowerCase();

  const match = CATEGORY_ICON_RULES.find((rule) => rule.matcher.test(normalized));
  return match ? match.icon : '💊';
};

export const buildCategoryRoute = (category) => {
  if (!category) {
    return '/categories';
  }

  const slug = typeof category === 'string'
    ? slugifyCategoryName(category)
    : category.slug || slugifyCategoryName(category.name);

  return `/category/${encodeURIComponent(slug)}`;
};

export const mapCategoriesBySlug = (categories = []) => {
  const map = new Map();

  categories.forEach((category) => {
    const name = normalizeCategoryName(category?.name || '');
    const slug = category?.slug || slugifyCategoryName(name);

    if (!map.has(slug)) {
      map.set(slug, {
        ...category,
        name,
        slug,
        count: 0
      });
    }

    const entry = map.get(slug);
    entry.count = (entry.count || 0) + (category.count || 1);
  });

  return map;
};

export const mergeCategoryCollections = (...collections) => {
  const merged = new Map();

  collections.forEach((collection) => {
    collection.forEach((category) => {
      const name = normalizeCategoryName(category?.name || '');
      const slug = category?.slug || slugifyCategoryName(name);

      if (!merged.has(slug)) {
        merged.set(slug, {
          ...category,
          name,
          slug,
          count: category.count || 0
        });
        return;
      }

      const existing = merged.get(slug);
      merged.set(slug, {
        ...existing,
        ...category,
        name,
        slug,
        count: (existing.count || 0) + (category.count || 0)
      });
    });
  });

  return Array.from(merged.values());
};